import { useOrganizationMemberRemove, useUserActiveOrganization } from "@/api";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { useState } from "react";

interface Props {
  closeModal: () => void;
  memberId: string;
  memberName: string;
}

export function RemoveMemberModal({ closeModal, memberId, memberName }: Props) {
  const [loading, setLoading] = useState(false);
  const { activeOrganization } = useUserActiveOrganization();
  const [memberRemove] = useOrganizationMemberRemove({
    organizationId: activeOrganization?.id || "",
    memberId
  });

  function handleRemoveMember() {
    setLoading(true);
    memberRemove()
      .then((res) => {
        if (res.data?.organizationMemberRemove.__typename !== "Organization") {
          console.error(
            `GraphQL error occurred while attempting to remove user: ${memberId} from organization: ${activeOrganization?.id}. GraphQL Error: ${res.data?.organizationMemberRemove.__typename}`
          );
          toast({
            title: "Unable to remove member from organization",
            description: `An error occurred while trying to remove ${memberName} from the organization. Please try again or report the issue.`
          });
        } else {
          toast({ title: `${memberName} removed from the organization` });
          closeModal();
        }
      })
      .catch((error) => {
        console.error(error);
        toast({
          title: "Unable to remove member from organization",
          description: `An error occurred while trying to remove ${memberName} from the organization. Please try again or report the issue.`
        });
      })
      .finally(() => setLoading(false));
  }

  return (
    <Dialog.Root open onOpenChange={closeModal}>
      <Dialog.Content className="sm:max-w-xl dark:bg-brand-gray-900">
        <Dialog.Header className="space-y-2 relative">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100">
            Remove member from organization
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300">
            Are you sure you want to remove{" "}
            <span className="font-semibold">{memberName}</span> from{" "}
            {activeOrganization?.name}?
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer className="mt-6">
          {loading && <LoadingCircle className="my-auto mr-2" />}
          <Dialog.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Dialog.Close>
          <Button
            variant="destructive"
            disabled={loading}
            onClick={handleRemoveMember}
          >
            Remove
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
